import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { currentSelectedLanguage } from "../../../../StateManagement/actions";
import { contactsService } from "../../../../Services/contactsService";
import "./ContactManagement.css";

export function DLDetailsModal({ isOpen, onClose, group, reader, contacts = [], contactGroups = [], contactsOnly = false }) {
  const selectedLanguage = useSelector(currentSelectedLanguage);
  const [showDomainsModal, setShowDomainsModal] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [allContactsList, setAllContactsList] = useState(contacts || []);
  const [allGroupsList, setAllGroupsList] = useState(contactGroups || []);

  // Derive DL Email and Name
  const dlEmail = (group?.aliasEmail || group?.AliasEmail || group?.email || group?.Email || group?.dlEmail || reader?.email || "").trim();
  const dlName = (group?.name || group?.Name || group?.groupName || (reader?.firstName && reader.firstName !== "(Distribution List)" ? reader.firstName : "") || "Distribution List").trim();

  // Matched Group from Directory if available
  const matchedGroup = useMemo(() => {
    if (group && (group.domains || group.memberContactIds || group.contactIds)) return group;
    const groupsToSearch = allGroupsList.length > 0 ? allGroupsList : (contactGroups || []);
    if (!groupsToSearch || groupsToSearch.length === 0) return group || null;
    return (
      groupsToSearch.find((g) => {
        if (!g) return false;
        const gName = (g.name || g.Name || "").toLowerCase().trim();
        const gAlias = (g.aliasEmail || g.AliasEmail || g.email || "").toLowerCase().trim();
        const targetEmail = dlEmail.toLowerCase();
        const targetName = dlName.toLowerCase();
        return (gAlias && gAlias === targetEmail) || (gName && gName === targetName);
      }) || group || null
    );
  }, [group, allGroupsList, contactGroups, dlEmail, dlName]);

  // Fetch all personal and company contacts + resolve hidden member contacts by ID and email
  useEffect(() => {
    if (!isOpen) return;
    let isCancelled = false;

    const fetchAllData = async () => {
      try {
        const companyName = localStorage.getItem("Companyname") || "";
        const [cAll, cPers, cComp, gAll, gPers, gComp] = await Promise.all([
          contactsService.getContacts("All", "", "", companyName).catch(() => []),
          contactsService.getContacts("Personal", "", "", companyName).catch(() => []),
          contactsService.getContacts("Company", "", "", companyName).catch(() => []),
          contactsService.getContactGroups("All", "", "", companyName).catch(() => []),
          contactsService.getContactGroups("Personal", "", "", companyName).catch(() => []),
          contactsService.getContactGroups("Company", "", "", companyName).catch(() => [])
        ]);

        const parseList = (res) => {
          if (Array.isArray(res)) return res;
          if (res && typeof res === "object") {
            for (const k of ["contacts", "data", "lstContacts", "contactGroups", "groups", "lstContactGroups", "lstGroups"]) {
              if (Array.isArray(res[k])) return res[k];
            }
          }
          return [];
        };

        const combinedContacts = [
          ...(Array.isArray(contacts) ? contacts : []),
          ...parseList(cAll),
          ...parseList(cPers),
          ...parseList(cComp)
        ];

        const combinedGroups = [
          ...(Array.isArray(contactGroups) ? contactGroups : []),
          ...parseList(gAll),
          ...parseList(gPers),
          ...parseList(gComp)
        ];

        // Deduplicate contacts by id and email
        const contactsMap = new Map();
        combinedContacts.forEach((c) => {
          if (!c) return;
          const cId = c.id || c._id;
          const cEmail = (c.email || c.Email || "").toLowerCase().trim();
          if (cId) contactsMap.set(String(cId).toLowerCase(), c);
          if (cEmail) contactsMap.set(cEmail, c);
        });

        // Determine effective group to check for memberContactIds
        const effGroup = (group && (group.memberContactIds || group.contactIds || group.members))
          ? group
          : combinedGroups.find((g) => {
              if (!g) return false;
              const gName = (g.name || g.Name || "").toLowerCase().trim();
              const gAlias = (g.aliasEmail || g.AliasEmail || g.email || "").toLowerCase().trim();
              const targetEmail = dlEmail.toLowerCase();
              const targetName = dlName.toLowerCase();
              return (gAlias && gAlias === targetEmail) || (gName && gName === targetName);
            }) || group || null;

        const rawMemberIds = effGroup?.memberContactIds || effGroup?.contactIds || effGroup?.members || [];
        
        // 1. Fetch missing members by ObjectId (for contacts saved with MongoDB ObjectId)
        const missingMemberIds = rawMemberIds
          .map((item) => (typeof item === "string" ? item.trim() : item?.id || item?._id || ""))
          .filter((id) => id && !id.includes("@") && !contactsMap.has(String(id).toLowerCase()));

        if (missingMemberIds.length > 0) {
          const individualContacts = await Promise.all(
            missingMemberIds.map((id) => contactsService.getContactById(id).catch(() => null))
          );
          individualContacts.forEach((c) => {
            if (c) {
              const cId = c.id || c._id;
              const cEmail = (c.email || c.Email || "").toLowerCase().trim();
              if (cId) contactsMap.set(String(cId).toLowerCase(), c);
              if (cEmail) contactsMap.set(cEmail, c);
              combinedContacts.push(c);
            }
          });
        }

        // 2. Fetch missing members by Email (for contacts saved with email that may be hidden or omitted from directory)
        const missingEmails = rawMemberIds
          .map((item) => (typeof item === "string" ? item.trim() : item?.email || item?.Email || ""))
          .filter((email) => email && email.includes("@") && !contactsMap.has(email.toLowerCase()));

        if (missingEmails.length > 0) {
          const searchResults = await Promise.all(
            missingEmails.map((email) => contactsService.getContacts("All", "", email, companyName).catch(() => []))
          );
          searchResults.forEach((res) => {
            parseList(res).forEach((c) => {
              if (c) {
                const cId = c.id || c._id;
                const cEmail = (c.email || c.Email || "").toLowerCase().trim();
                if (cId) contactsMap.set(String(cId).toLowerCase(), c);
                if (cEmail) contactsMap.set(cEmail, c);
                combinedContacts.push(c);
              }
            });
          });
        }

        if (!isCancelled) {
          setAllContactsList(Array.from(new Set(combinedContacts)));
          setAllGroupsList(combinedGroups);
        }
      } catch (err) {
        console.error("Error fetching all contacts/groups in DLDetailsModal:", err);
      }
    };

    fetchAllData();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, group, reader, dlEmail, dlName, contacts, contactGroups]);

  // Derive Allowed Domains strictly from Group / Reader data
  const allowedDomains = useMemo(() => {
    if (matchedGroup?.domains && Array.isArray(matchedGroup.domains)) {
      return matchedGroup.domains;
    }
    if (reader?.allowedDomains && Array.isArray(reader.allowedDomains)) {
      return reader.allowedDomains;
    }
    return [];
  }, [matchedGroup, reader]);

  // Derived available contacts list
  const allAvailableContacts = useMemo(() => {
    return allContactsList.length > 0 ? allContactsList : (contacts || []);
  }, [allContactsList, contacts]);

  // Derive Member Contacts strictly from Group / Reader data & match against complete contacts list
  const usersList = useMemo(() => {
    let rawMembers = [];
    if (group?.groupMembers && Array.isArray(group.groupMembers) && group.groupMembers.length > 0) {
      rawMembers = group.groupMembers;
    } else if (reader?.groupMembers && Array.isArray(reader.groupMembers) && reader.groupMembers.length > 0) {
      rawMembers = reader.groupMembers;
    } else {
      rawMembers = matchedGroup?.memberContactIds || matchedGroup?.contactIds || matchedGroup?.members || [];
    }

    if (!rawMembers || rawMembers.length === 0) return [];

    return rawMembers.map((idOrEmail) => {
      if (typeof idOrEmail === "object" && idOrEmail !== null) {
        const objEmail = (idOrEmail.email || idOrEmail.Email || "").trim();
        const objId = String(idOrEmail.id || idOrEmail._id || "").toLowerCase().trim();
        const cleanEmail = objEmail.toLowerCase();
        const found = allAvailableContacts.find((c) => {
          if (!c) return false;
          const cId = String(c.id || c._id || "").toLowerCase().trim();
          const cEmail = String(c.email || c.Email || "").toLowerCase().trim();
          return (objId && cId === objId) || (cleanEmail && cEmail === cleanEmail);
        });

        const rawFirst = found ? (found.firstName || found.FirstName || "") : (idOrEmail.firstName || idOrEmail.FirstName || "");
        const rawLast = found ? (found.lastName || found.LastName || "") : (idOrEmail.lastName || idOrEmail.LastName || "");
        const cleanFirst = (rawFirst === "(Distribution List)" || rawFirst === "(DL)") ? "" : rawFirst.trim();
        const cleanLast = (rawLast === "(Distribution List)" || rawLast === "(DL)") ? "" : rawLast.trim();

        return {
          ...idOrEmail,
          email: found ? (found.email || found.Email || objEmail) : objEmail,
          firstName: cleanFirst,
          lastName: cleanLast
        };
      }

      const cleanKey = String(idOrEmail).toLowerCase().trim();
      const found = allAvailableContacts.find((c) => {
        if (!c) return false;
        const cId = String(c.id || c._id || "").toLowerCase().trim();
        const cEmail = String(c.email || c.Email || "").toLowerCase().trim();
        return (cId && cId === cleanKey) || (cEmail && cEmail === cleanKey);
      });

      if (found) {
        const rawFirst = found.firstName || found.FirstName || "";
        const rawLast = found.lastName || found.LastName || "";
        const cleanFirst = (rawFirst === "(Distribution List)" || rawFirst === "(DL)") ? "" : rawFirst.trim();
        const cleanLast = (rawLast === "(Distribution List)" || rawLast === "(DL)") ? "" : rawLast.trim();

        return {
          id: found.id || found._id || cleanKey,
          email: found.email || found.Email || (cleanKey.includes("@") ? cleanKey : ""),
          firstName: cleanFirst,
          lastName: cleanLast
        };
      }

      // If contact is not found, do NOT split with @! Just return empty firstName and lastName
      return {
        email: cleanKey,
        firstName: "",
        lastName: ""
      };
    }).filter(Boolean);
  }, [group, reader, matchedGroup, allAvailableContacts]);

  if (!isOpen || (!group && !reader)) return null;

  return (
    <>
      {/* Main DL Details Modal */}
      {!contactsOnly && (
        <div className="modalCus">
          <div className="modal-dialog modal-dialog-centered dl-details-modal-dialog">
            <div className="modal-content border-r4">
              {/* Header */}
              <div className="modal-header d-flex align-items-center justify-content-between">
                <h4 className="mb-0 text-danger fw-bold font-20">
                  {dlName}
                </h4>
                <button type="button" className="btn btn-outline-danger Readerclose modalclose download mt-0" onClick={onClose}>
                  <span className="material-icons font-20">highlight_off</span>
                </button>
              </div>
              {/* Body */}
              <div className="modal-body p-4">
                {/* DL Email Header Info Card */}
                <div className="p-3 mb-3 border rounded bg-light d-flex align-items-center justify-content-between">
                  <div>
                    <span className="font-11 text-muted text-uppercase fw-bold d-block">
                      {selectedLanguage?.LstLanguageCodes?.UI_DLEmailAddress || "Distribution List Email"}
                    </span>
                    <span className="font-14 font-w600 text-dark font-mono wordbreak">
                      {dlEmail}
                    </span>
                  </div>
                </div>
                {/* Cards Grid: Domains & Contacts */}
                <div className="row g-3">
                  {/* DOMAINS CARD */}
                  <div className="col-6">
                    <div className="p-3 border rounded text-center bg-white shadow-2xs h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="mb-2">
                          <span className="material-icons font-32 text-danger">language</span>
                        </div>
                        <div className="font-12 fw-bold text-muted text-uppercase">{selectedLanguage?.LstLanguageCodes?.UI_Domains || "Domains"}</div>
                        <div className="font-18 fw-bold text-dark mt-1">
                          {allowedDomains.length}
                        </div>
                      </div>
                      <button type="button" disabled={allowedDomains.length === 0} onClick={() => allowedDomains.length > 0 && setShowDomainsModal(true)}
                        className={`btn btn-sm border-r4 font-12 w-100 mt-3 d-flex align-items-center justify-content-center gap-1 
                          ${allowedDomains.length === 0 ? "btn-secondary opacity-50 cursor-not-allowed disabled-background" : "btn-outline-danger"}`}>
                        <span>{selectedLanguage?.LstLanguageCodes?.UI_ClickToViewContact || "Click to view"}</span>
                        <span className="material-icons font-16">chevron_right</span>
                      </button>
                    </div>
                  </div>

                  {/* CONTACTS CARD */}
                  <div className="col-6">
                    <div className="p-3 border rounded text-center bg-white shadow-2xs h-100 d-flex flex-column justify-content-between">
                      <div>
                        <div className="mb-2">
                          <span className="material-icons font-32 text-danger">people</span>
                        </div>
                        <div className="font-12 fw-bold text-muted text-uppercase">{selectedLanguage?.LstLanguageCodes?.UI_Contacts || "Contacts"}</div>
                        <div className="font-18 fw-bold text-dark mt-1">
                          {usersList.length}
                        </div>
                      </div>
                      <button type="button" disabled={usersList.length === 0} onClick={() => usersList.length > 0 && setShowContactsModal(true)}
                        className={`btn btn-sm border-r4 font-12 w-100 mt-3 d-flex align-items-center justify-content-center gap-1
                           ${usersList.length === 0 ? "btn-secondary opacity-50 cursor-not-allowed disabled-background" : "btn-outline-danger"}`}>
                        <span>{selectedLanguage?.LstLanguageCodes?.UI_ClickToViewContact || "Click to view"}</span>
                        <span className="material-icons font-16">chevron_right</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer d-flex align-items-center justify-content-end">
                <button type="button" className="btn btn-danger px-4 border-r4 font-14" onClick={onClose}>
                  {selectedLanguage?.LstLanguageCodes?.UI_Close || "Close"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Child Mobile-Like Sub-Modal: Domains List */}
      {!contactsOnly && showDomainsModal && (
        <div className="modalCus dl-child-modal-overlay">
          <div className="modal-dialog modal-dialog-centered custom-width-model mobile-modal dl-child-domains-dialog">
            <div className="modal-content border-r4 dl-child-modal-content">
              <div className="modal-header d-flex align-items-center justify-content-between">
                <h4 className="mb-0 text-danger fw-bold font-18 d-flex align-items-center gap-2">
                  <span className="material-icons font-20">language</span>
                  {selectedLanguage?.LstLanguageCodes?.UI_Domains || "Domains"} ({allowedDomains.length})
                </h4>
                <button type="button" className="btn btn-outline-danger Readerclose modalclose download mt-0" onClick={() => setShowDomainsModal(false)}>
                  <span className="material-icons font-20">highlight_off</span>
                </button>
              </div>
              <div className="modal-body p-3 dl-child-domains-body">
                {allowedDomains.length > 0 && (
                  allowedDomains.map((dom, idx) => (
                    <div key={`dom-${idx}`} className="p-2 mb-2 border rounded d-flex align-items-center gap-2 bg-light">
                      <span className="font-14 font-w500 text-dark font-mono">
                        @{dom.replace(/^@/, "")}
                      </span>
                    </div>
                  ))
                )}
              </div>
              <div className="modal-footer d-flex justify-content-end p-2 dl-child-footer">
                <button type="button" className="btn btn-danger px-3 py-1 font-13 border-r4" onClick={() => setShowDomainsModal(false)}>
                  {selectedLanguage?.LstLanguageCodes?.UI_Close || "Close"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Child Mobile-Like Sub-Modal: Contacts List */}
      {(showContactsModal || contactsOnly) && (
        <div className="modalCus dl-child-modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal-dialog modal-dialog-centered custom-width-model mobile-modal dl-child-contacts-dialog">
            <div className="modal-content border-r4 dl-child-modal-content">
              <div className="modal-header d-flex align-items-center justify-content-between">
                <h4 className="mb-0 text-danger fw-bold font-18 d-flex align-items-center gap-2">
                  <span className="material-icons font-20">people</span>
                  {selectedLanguage?.LstLanguageCodes?.UI_Contacts || "Contacts"} ({usersList.length})
                </h4>
                <button
                  type="button"
                  className="btn btn-outline-danger Readerclose modalclose download mt-0"
                  onClick={() => {
                    if (contactsOnly) {
                      onClose();
                    } else {
                      setShowContactsModal(false);
                    }
                  }}
                >
                  <span className="material-icons font-20">highlight_off</span>
                </button>
              </div>
              <div className="modal-body p-3 dl-child-contacts-body">
                {usersList.length > 0 && (
                  usersList.map((user, idx) => {
                    const emailStr = typeof user === "string" ? user : (user.email || user.Email || "");
                    let fullName = "";
                    if (!contactsOnly) {
                      let fName = user.firstName || user.FirstName || "";
                      let lName = user.lastName || user.LastName || "";
                      if (!fName && !lName && emailStr && allAvailableContacts && allAvailableContacts.length > 0) {
                        const foundContact = allAvailableContacts.find((c) => {
                          const cEmail = (c?.email || c?.Email || "").toLowerCase().trim();
                          return cEmail && cEmail === emailStr.toLowerCase().trim();
                        });
                        if (foundContact) {
                          fName = foundContact.firstName || foundContact.FirstName || "";
                          lName = foundContact.lastName || foundContact.LastName || "";
                        }
                      }
                      const cleanFirst = (fName === "(Distribution List)" || fName === "(DL)") ? "" : fName.trim();
                      const cleanLast = (lName === "(Distribution List)" || lName === "(DL)") ? "" : lName.trim();
                      fullName = `${cleanFirst} ${cleanLast}`.trim();
                      if (fullName) {
                        fullName = "<" + fullName + ">";
                      }
                    }
                    return (
                      <div key={`usr-${idx}`} className="p-2 mb-2 border rounded d-flex align-items-center justify-content-between bg-light">
                        <div className="overflow-hidden">
                          <div className="font-14 font-w500 text-dark text-truncate">
                            {contactsOnly ? emailStr : `${emailStr} ${fullName}`.trim()}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="modal-footer d-flex justify-content-end p-2 dl-child-footer">
                <button
                  type="button"
                  className="btn btn-danger px-3 py-1 font-13 border-r4"
                  onClick={() => {
                    if (contactsOnly) {
                      onClose();
                    } else {
                      setShowContactsModal(false);
                    }
                  }}
                >
                  {selectedLanguage?.LstLanguageCodes?.UI_Close || "Close"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DLDetailsModal;
